/**
 * Accounts gRPC adapter for the rrecce `protos.accounts.Accounts` service.
 *
 * Exposes the RPCs the session needs:
 *   - verifyEmail    (unauthenticated; promotes the account to Active once Firebase email is verified)
 *   - getMyAccount    (authenticated; the account identity — the source of truth for the user)
 */

import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { Accounts } from '../gen/accounts/accounts_service_pb';
import { WorkFunction } from '../gen/general/work_function_pb';
import { WorkLocation } from '../gen/general/work_location_pb';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  return next(req);
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(Accounts, transport);
  }
  return cachedClient;
}

export async function verifyEmail(email) {
  await client().verifyEmail({ params: { email } });
}

export async function getMyAccount() {
  const response = await client().getMyAccount({ params: {} });
  const account = response.result?.myAccount;
  if (!account) return null;
  return {
    id: account.accountId,
    email: account.contact?.email || '',
    display_name: account.name,
    avatar_path: account.pfpUrl || null,
    work_function: enumName(WorkFunction, account.mainWorkFunction?.function),
    work_location: enumName(WorkLocation, account.mainWorkLocation),
  };
}

// The value's name in the generated enum; null for unset (the proto zero) and for a value
// this client does not know
function enumName(enumObject, value) {
  return value ? enumObject[value] ?? null : null;
}
